// src/components/HistoryPanel.js

import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Offcanvas,
  ListGroup,
  Button,
  Modal,
  OverlayTrigger,
  Popover,
  Pagination,
} from 'react-bootstrap';
import { LanguageContext } from '../contexts/LanguageContext';
import { SettingsContext } from '../contexts/SettingsContext';
import { debounce, safeGetItem, safeSetItem } from '../utils/localStorageUtils';
import { useSwipeToDelete } from '../hooks/useSwipeToDelete';
import ConfirmDialog from './ConfirmDialog';
import useConfirmDialog from '../hooks/useConfirmDialog';
import Hammer from 'hammerjs'; // <-- ADDED to restore swipe-to-close with Hammer.js
import './HistoryPanel.css';

/**
 * HistoryPanel Component
 * Displays a slide‐out panel from the right showing previous search entries.
 * Each entry shows its timestamp and number of results.
 * Users can click "View" to re‐open that search or "Delete" to remove it.
 */
const HistoryPanel = ({ show, handleClose, onSelect }) => {
  const [history, setHistory] = useState([]);
  const { translations } = useContext(LanguageContext);
  const { settings } = useContext(SettingsContext);
  
  // Dialog hook
  const { dialogState: confirmState, showConfirm, hideConfirm } = useConfirmDialog();

  // Bulk Deletion Mode
  const [bulkDeletionMode, setBulkDeletionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Single item delete highlighting
  const [deleteSelectedId, setDeleteSelectedId] = useState(null);

  // Detect mobile/tablet (<=768px)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Time filter state
  const [timeFilter, setTimeFilter] = useState('all'); // 'all', 'today', 'yesterday', 'week', 'month'

  // Responsive pagination - adjust items per page based on screen width
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 576) {
        setItemsPerPage(10); // Mobile view - more items for better UX
      } else if (window.innerWidth < 992) {
        setItemsPerPage(8); // Tablet view
      } else {
        setItemsPerPage(10); // Desktop view
      }
    };
    
    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter history by time
  const getFilteredHistory = () => {
    if (timeFilter === 'all') return history;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return history.filter(item => {
      const itemDate = new Date(item.timestamp);
      
      switch(timeFilter) {
        case 'today':
          return itemDate >= today;
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          return itemDate >= yesterday && itemDate < today;
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(weekAgo.getDate() - 7);
          return itemDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setDate(monthAgo.getDate() - 30);
          return itemDate >= monthAgo;
        default:
          return true;
      }
    });
  };

  // Calculate pagination values using filtered history
  const filteredHistory = getFilteredHistory();
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredHistory.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredHistory.length / itemsPerPage);
  
  // Generate page numbers to show (cleaner design)
  const getPageNumbers = () => {
    const isMobile = window.innerWidth < 576;
    const maxVisiblePages = isMobile ? 5 : 8; // Show 5 on mobile, 8 on desktop
    
    let pageNumbers = [];
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is less than max
      pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      // Show pages around current page
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      
      // Adjust if we're near the end
      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
      
      pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    }
    
    return pageNumbers;
  };

  // NEW: Freeze history refresh once an item is selected
  const [freezeHistory, setFreezeHistory] = useState(false);
  useEffect(() => {
    if (!show) {
      setFreezeHistory(false);
    }
  }, [show]);

  // Bulk selection logic
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  const toggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };
  const startBulkDeletion = (id) => {
    setBulkDeletionMode(true);
    setSelectedIds([id]);
    const index = history.findIndex((item) => item.id === id);
    setLastSelectedIndex(index);
  };
  const cancelBulkDeletion = () => {
    setBulkDeletionMode(false);
    setSelectedIds([]);
    setLastSelectedIndex(null);
  };
  const handleBulkClick = (e, index, id) => {
    if (e.shiftKey && lastSelectedIndex !== null) {
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      const idsToSelect = history.slice(start, end + 1).map((item) => item.id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...idsToSelect])));
    } else {
      toggleSelection(id);
      setLastSelectedIndex(index);
    }
  };

  // Debounced localStorage update
  const debouncedSetHistoryInStorage = useRef(
    debounce((data) => {
      safeSetItem('searchHistory', data);
    }, 300)
  );

  // For image preview in a modal (used by both mobile & desktop click)
  const [previewImage, setPreviewImage] = useState(null);
  const closePreview = () => setPreviewImage(null);

  // RE-ADDED for swipe-to-close panel:
  const panelRef = useRef(null);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  // Sub-component for each history item
  const HistoryListItem = ({
    item,
    onView,
    onDelete,
    enableBulk,
    bulkDeletionMode,
    selected,
    index,
    onBulkClick,
  }) => {
    const { itemRef, deltaX, isPanning, swiped } = useSwipeToDelete(
      item.id,
      { 
        onDelete,
        onConfirmDelete: async () => {
          return await showConfirm({
            title: translations.deleteItem || 'Delete Item',
            message: translations.confirmDeletion || 'Are you sure you want to delete this item?',
            confirmText: translations.delete || 'Delete',
            cancelText: translations.cancel || 'Cancel',
            variant: 'danger',
            onConfirm: () => {}
          });
        },
        onHighlight: setDeleteSelectedId
      },
      enableBulk,
      bulkDeletionMode,
      startBulkDeletion,
      translations
    );

    const bulkClass = bulkDeletionMode && selected ? 'bulk-selected' : '';
    const swipingClass = isPanning ? 'swiping' : '';

    // Mobile/Tablet: click => open modal
    const handleImageClickMobile = () => {
      if (isMobile && item.uploadedImage) {
        setPreviewImage(item.uploadedImage);
      }
    };

    // Desktop: hover => popover, click => open modal
    const handleImageClickDesktop = () => {
      if (!isMobile && item.uploadedImage) {
        setPreviewImage(item.uploadedImage);
      }
    };

    // Desktop version with popover on hover
    const DesktopImage = item.uploadedImage ? (
      <OverlayTrigger
        placement="auto"
        overlay={
          <Popover id={`popover-${item.id}`}>
            <img
              src={item.uploadedImage}
              alt="Preview"
              className="popover-img"
            />
          </Popover>
        }
      >
        <img
          src={item.uploadedImage}
          alt="Uploaded"
          style={{
            width: '50px',
            height: '50px',
            objectFit: 'cover',
            marginRight: '8px',
          }}
          className="uploaded-image-thumbnail"
          onClick={handleImageClickDesktop}
        />
      </OverlayTrigger>
    ) : null;

    // Mobile version (no hover popover)
    const MobileImage = item.uploadedImage ? (
      <img
        src={item.uploadedImage}
        alt="Uploaded"
        style={{
          width: '50px',
          height: '50px',
          objectFit: 'cover',
          marginRight: '8px',
        }}
        className="uploaded-image-thumbnail"
        onClick={handleImageClickMobile}
      />
    ) : null;

    return (
      <ListGroup.Item
        ref={itemRef}
        key={item.id}
        className={`d-flex justify-content-between align-items-center history-item ${bulkClass} ${swipingClass} ${
          swiped ? 'swipe-delete' : ''
        } ${deleteSelectedId === item.id ? 'delete-selected' : ''}`}
        style={{
          transform: `translateX(${deltaX}px)`,
          transition: isPanning ? 'none' : 'transform 0.3s ease',
          position: 'relative',
        }}
        onClick={(e) => {
          if (bulkDeletionMode) {
            onBulkClick(e, index, item.id);
          }
        }}
      >
        <div className="d-flex align-items-center">
          {isMobile ? MobileImage : DesktopImage}
          <div>
            <div>{new Date(item.timestamp).toLocaleString()}</div>
            <small>
              {item.products ? item.products.filter(p => !p.hidden).length : 0}{' '}
              {translations.results || 'results'}
            </small>
          </div>
        </div>
        <div className="history-item-buttons">
          {!bulkDeletionMode && (
            <>
              <Button variant="primary" size="sm" onClick={(e) => { e.stopPropagation(); handleSelectItem(item); }}>
                {translations.view || 'View'}
              </Button>
              {/* On desktop, show Delete button. On mobile, user can swipe to delete. */}
              {!isMobile && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={async () => {
                    setDeleteSelectedId(item.id); // Highlight the item
                    try {
                      const confirmed = await showConfirm({
                        title: translations.deleteItem || 'Delete Item',
                        message: translations.confirmDeletion || 'Are you sure you want to delete this item?',
                        confirmText: translations.delete || 'Delete',
                        cancelText: translations.cancel || 'Cancel',
                        variant: 'danger',
                        onConfirm: () => {}
                      });
                      
                      if (confirmed) {
                        onDelete(item.id);
                      }
                    } catch (error) {
                      // Handle any errors from the dialog
                      console.error('Delete confirmation error:', error);
                    } finally {
                      // Always clear highlighting whether confirmed, cancelled, or errored
                      setDeleteSelectedId(null);
                    }
                  }}
                >
                  {translations.delete || 'Delete'}
                </Button>
              )}
            </>
          )}
        </div>
      </ListGroup.Item>
    );
  };

  // Load history from localStorage when panel is shown
  useEffect(() => {
    if (show) {
      try {
        const stored = safeGetItem('searchHistory', []);
        stored.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setHistory(stored);
        setCurrentPage(1); // Reset to first page when history is loaded
      } catch (err) {
        console.error('Error reading history from localStorage:', err);
        setHistory([]);
      }
    }
  }, [show]);

  // Reset bulk mode each time panel is opened
  useEffect(() => {
    if (show) {
      cancelBulkDeletion();
      setCurrentPage(1); // Reset to first page when panel opens
    }
  }, [show]);

  // Reset to first page when time filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [timeFilter]);

  // Listen for custom "historyUpdated" event to refresh
  useEffect(() => {
    const updateHistory = () => {
      // If panel is closed or history is frozen, skip refresh
      if (!show || freezeHistory) {
        return;
      }
      try {
        const stored = safeGetItem('searchHistory', []);
        stored.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setHistory(stored);
      } catch (err) {
        console.error('Error reading history from localStorage:', err);
        setHistory([]);
      }
    };
    window.addEventListener('historyUpdated', updateHistory);
    return () => {
      window.removeEventListener('historyUpdated', updateHistory);
    };
  }, [show, freezeHistory]);

  // Delete a single history entry
  const handleDelete = (id) => {
    const updated = history.filter((item) => item.id !== id);
    setHistory(updated);
    debouncedSetHistoryInStorage.current(updated);
    // Reset to first page if current page becomes empty
    const newTotalPages = Math.ceil(updated.length / itemsPerPage);
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages);
    }
  };

  // "View" => pass the item to parent, freeze history and close panel
  const handleSelectItem = (item) => {
    setFreezeHistory(true); // NEW: Freeze any history refresh
    onSelect(item);
    handleClose();
  };

  // Bulk delete selected
  const deleteSelectedItems = async () => {
    const count = selectedIds.length;
    const isPlural = count > 1;
    
    // Get the message template and replace {count} with actual count
    const messageTemplate = isPlural ? 
      (translations.confirmDeleteMultipleItems || 'Are you sure you want to delete {count} items?') : 
      (translations.confirmDeleteOneItem || 'Are you sure you want to delete this item?');
    
    const message = messageTemplate.replace('{count}', count);
    
    const confirmed = await showConfirm({
      title: isPlural ? (translations.deleteMultipleItems || 'Delete Items') : (translations.deleteOneItem || 'Delete Item'),
      message: message,
      confirmText: translations.delete || 'Delete',
      cancelText: translations.cancel || 'Cancel',
      variant: 'danger',
      onConfirm: () => {}
    });

    if (confirmed) {
      const updated = history.filter((item) => !selectedIds.includes(item.id));
      setHistory(updated);
      debouncedSetHistoryInStorage.current(updated);
      cancelBulkDeletion();
      // Reset to first page if current page becomes empty
      const newTotalPages = Math.ceil(updated.length / itemsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      } else if (updated.length === 0) {
        setCurrentPage(1);
      }
    }
  };

  // RE-ADDED from previous version: handle panel-level swipe with Hammer
  useEffect(() => {
    if (show && panelRef.current) {
      const hammer = new Hammer(panelRef.current);
      hammer.get('swipe').set({ direction: Hammer.DIRECTION_HORIZONTAL });
      hammer.on('swiperight', () => {
        handleClose();
      });
      return () => {
        hammer.off('swiperight');
        hammer.destroy();
      };
    }
  }, [show, handleClose]);

  // Prevent scroll propagation
  const handleWheel = (e) => {
    e.stopPropagation();
  };

  // Enhanced touch handling to prevent propagation
  const handleTouchStart = (e) => {
    // Prevent touch events from propagating to background
    e.stopPropagation();
    
    if (e.touches.length === 1) {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    }
  };
  
  const handleTouchEnd = (e) => {
    // Prevent touch events from propagating to background
    e.stopPropagation();
    
    if (!touchStartX.current || !touchStartY.current) return;
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const dx = touchEndX - touchStartX.current;
    const dy = touchEndY - touchStartY.current;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0) {
        handleClose();
      }
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };
  
  const handleTouchMove = (e) => {
    // Prevent touch move events from propagating to background
    e.stopPropagation();
  };

  // Clean up any body styles when component unmounts or panel closes
  useEffect(() => {
    return () => {
      // Ensure body can scroll when component unmounts
      document.body.style.overflow = '';
    };
  }, []);

  // Effect to manage body scroll state when panel opens/closes
  useEffect(() => {
    if (show) {
      // When panel opens, make sure it doesn't affect body scroll
      document.body.style.overflow = 'auto';
    } else {
      // When panel closes, restore normal scrolling
      document.body.style.overflow = '';
    }
  }, [show]);

  return (
    <>
      <Offcanvas 
        show={show} 
        onHide={handleClose} 
        placement="end" 
        scroll={false} 
        backdrop={true}
        className="history-panel-offcanvas"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>{translations.searchHistory || 'Search History'}</Offcanvas.Title>
        </Offcanvas.Header>
        
        {/* Time Filter Controls */}
        <div className="history-filter-controls">
          <div className="d-flex flex-wrap gap-2 justify-content-center time-filter-chip-group">
            {[
              { key: 'today', label: translations.today || 'Today' },
              { key: 'yesterday', label: translations.yesterday || 'Yesterday' },
              { key: 'week', label: translations.last7Days || '7d' },
              { key: 'month', label: translations.last30Days || '30d' }
            ].map(filter => {
              const active = timeFilter === filter.key;
              return (
                <button
                  type="button"
                  key={filter.key}
                  className={`time-filter-chip ${active ? 'active' : ''}`}
                  onClick={() => setTimeFilter(active ? 'all' : filter.key)}
                  aria-pressed={active}
                >
                  <span className="time-filter-chip-label">{filter.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        {/* Sticky bulk deletion buttons integrated into header area */}
        {bulkDeletionMode && (
          <div className="bulk-deletion-controls d-flex justify-content-between align-items-center">
            <Button variant="secondary" onClick={cancelBulkDeletion}>
              {translations.cancel}
            </Button>
            <Button variant="danger" onClick={deleteSelectedItems}>
              {translations.delete}
            </Button>
          </div>
        )}
        {/* Attach ref and enhanced touch/wheel handlers to isolate scrolling */}
        <Offcanvas.Body
          ref={panelRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchMove}
          onWheel={handleWheel}
          className={`scrollable-history-list font-size-${settings?.fontSize || 'medium'}`}
        >
          {filteredHistory.length === 0 ? (
            <p>
              {history.length === 0 
                ? (translations.noHistoryAvailable || 'No history available.')
                : (translations.noResultsForFilter || 'No results for selected time period.')
              }
            </p>
          ) : (
            <ListGroup className="history-list">
              {currentItems.map((item, index) => (
                <HistoryListItem
                  key={item.id}
                  item={item}
                  onView={handleSelectItem}
                  onDelete={handleDelete}
                  translations={translations}
                  enableBulk={history.length > 1}
                  bulkDeletionMode={bulkDeletionMode}
                  selected={selectedIds.includes(item.id)}
                  index={indexOfFirstItem + index}
                  onBulkClick={handleBulkClick}
                />
              ))}
            </ListGroup>
          )}
          
          {/* Pagination Controls - Clean Design */}
          {filteredHistory.length > itemsPerPage && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination>
                {currentPage > 1 && (
                  <Pagination.Prev 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  />
                )}
                
                {getPageNumbers().map((number) => (
                  <Pagination.Item
                    key={number}
                    active={number === currentPage}
                    onClick={() => setCurrentPage(number)}
                  >
                    {number}
                  </Pagination.Item>
                ))}
                
                {currentPage < totalPages && (
                  <Pagination.Next 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  />
                )}
              </Pagination>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Shared Modal for image preview (mobile/tablet OR desktop click) */}
      <Modal show={!!previewImage} onHide={closePreview} centered>
        <Modal.Header closeButton>
          <Modal.Title>{translations.view || 'View'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {previewImage && (
            <img
              src={previewImage}
              alt="Preview"
              style={{ width: '100%', height: 'auto' }}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePreview}>
            {translations.cancel || 'Cancel'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirm Dialog */}
      <ConfirmDialog
        show={confirmState.show}
        onHide={hideConfirm}
        onConfirm={confirmState.onConfirm}
        onCancel={confirmState.onCancel}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        icon={confirmState.icon}
      />
    </>
  );
};

export default HistoryPanel;
